import { useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../../api';
import type { AlertChannelTestResult } from '../../api';
import { useResource } from '../../hooks';
import { Field, Input, Section } from '../primitives';
import { ErrorNotice, StatusBadge } from '../ui';
import { QuietEmpty } from '../project/ui';
import { relativeTime } from '../project/projectHealth';

/**
 * AlertChannelsPanel (CO-196): where an alert goes. An owner sets the
 * organization's webhook once (Slack, Discord, Telegram, ntfy or any https
 * URL), tests it, and sees at a glance whether an alert raised now would
 * reach anyone and how many open alerts missed every channel.
 */
export function AlertChannelsPanel({ isOwner }: { isOwner: boolean }) {
  const [reload, setReload] = useState(0);
  const status = useResource(() => (isOwner ? api.getAlertChannels() : Promise.resolve(null)), [isOwner, reload], undefined, { key: 'alert-channels' });
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState<'save' | 'test' | 'remove' | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [test, setTest] = useState<AlertChannelTestResult | null>(null);

  if (!isOwner) {
    return (
      <Section title="Alerts" testId="alert-channels">
        <QuietEmpty>Only an owner can set up alerts</QuietEmpty>
      </Section>
    );
  }
  const data = status.data;

  async function save(event: FormEvent) {
    event.preventDefault();
    await run('save', () => api.setAlertChannels(url.trim()), () => setUrl(''));
  }

  async function run(kind: 'save' | 'remove', call: () => Promise<unknown>, after?: () => void) {
    setBusy(kind);
    setError(null);
    setTest(null);
    try {
      await call();
      after?.();
      setReload((n) => n + 1);
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(null);
    }
  }

  async function sendTest() {
    setBusy('test');
    setError(null);
    try {
      setTest(await api.testAlertChannels());
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Section title="Alerts" testId="alert-channels">
      {status.error && !data ? <ErrorNotice error={status.error} /> : null}
      {error ? <ErrorNotice error={error} /> : null}
      {data ? (
        <>
          <p className="summary-line" data-testid="alert-channels-summary">
            <StatusBadge kind="alertChannels" value={data.reachable ? 'reachable' : 'nobody'} />
            {data.undelivered_open > 0 ? <span>{data.undelivered_open} open without a recipient</span> : null}
            {data.last_delivered_at ? <span className="muted">Last delivered {relativeTime(data.last_delivered_at)}</span> : null}
          </p>
          <dl className="settings-facts" aria-label="Alert channels">
            <div>
              <dt>Webhook</dt>
              <dd>{data.webhook_configured ? <code translate="no">{data.webhook_host}</code> : <span className="muted">Not set</span>}</dd>
            </div>
            <div>
              <dt>Email to owners</dt>
              <dd>{data.email_configured ? 'On' : <span className="muted">Off: no mail server</span>}</dd>
            </div>
            {data.environment_webhooks > 0 ? (
              <div>
                <dt>Environment webhooks</dt>
                <dd>{data.environment_webhooks}</dd>
              </div>
            ) : null}
          </dl>
          <form className="stack" onSubmit={save}>
            <Field label={data.webhook_configured ? 'Replace the webhook URL' : 'Webhook URL'} hint="Slack, Discord, ntfy, Telegram or any URL">
              <Input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://ntfy.sh/your-private-topic"
                autoComplete="off"
                spellCheck={false}
                required
              />
            </Field>
            <div className="row-actions">
              <button type="submit" className="button button--primary" disabled={busy !== null || url.trim() === ''}>
                {busy === 'save' ? 'Saving' : 'Save webhook'}
              </button>
              <button type="button" className="button button--secondary" disabled={busy !== null || !data.reachable} onClick={sendTest} data-testid="alert-test">
                {busy === 'test' ? 'Sending' : 'Send test'}
              </button>
              {data.webhook_configured ? (
                <button type="button" className="button button--danger-quiet" disabled={busy !== null} onClick={() => run('remove', () => api.setAlertChannels(''))}>
                  Remove webhook
                </button>
              ) : null}
            </div>
          </form>
          {test ? (
            <p className="summary-line" role="status" data-testid="alert-test-result">
              <span>Webhook: {test.webhook}</span>
              <span>Email: {test.email}</span>
            </p>
          ) : null}
        </>
      ) : status.loading ? (
        <QuietEmpty>Reading…</QuietEmpty>
      ) : null}
    </Section>
  );
}
